import React, { useState, useEffect } from 'react';

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { Select, MenuItem, FormControl, FormHelperText } from '@mui/material';

import useResponsive from 'src/hooks/useResponsive';

import { ElementProps } from '../../../@types/forms/types';

//-------------------------------------------------------

type DropdownElementProps = {
  el: ElementProps;
  scaledDimensions: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    fontSize: number;
  };
  elementContentValue?: string[];
  handleDropdownChange: (value: string, el: ElementProps) => void;
  handleTextAlign: (alignEnum: number) => string;
  pageNumberScale: number;
  collaboratorColor?: string;
  disabled?: boolean;
  errorMessage?: string;
};

const DropdownElement: React.FC<DropdownElementProps> = ({
  el,
  scaledDimensions,
  elementContentValue,
  handleDropdownChange,
  handleTextAlign,
  pageNumberScale,
  collaboratorColor,
  disabled = false,
  errorMessage,
}) => {
  const [value, setValue] = useState<string | undefined>(undefined);
  const isMobile = useResponsive('down', 'sm');

  // initial value
  useEffect(() => {
    if (elementContentValue) setValue(elementContentValue[0]);
  }, [elementContentValue]);

  return (
    <FormControl
      variant="standard"
      fullWidth
      error={!!errorMessage}
      sx={{
        backgroundColor: collaboratorColor,
        borderRadius: 1,
      }}
    >
      <Select
        IconComponent={ExpandMoreRoundedIcon}
        value={value || ''}
        id={`select-${el.id}`}
        size="small"
        disabled={disabled}
        onChange={(e) => {
          const newVal = e.target.value as string;
          handleDropdownChange(newVal, el);
          setValue(newVal);
        }}
        sx={[
          {
            color: el.color,
            fontSize: scaledDimensions.fontSize,
            textAlign: handleTextAlign(el.textAlignment),
            fontWeight: el.textStyle.includes('Bold') ? 'bold' : 'normal',
            fontStyle: el.textStyle.includes('Italic') ? 'italic' : 'normal',
            fontFamily: el.font,
          },
          el.textDecoration === 1 ? { textDecoration: 'underline' } : { textDecoration: 'none' },
        ]}
      >
        {el.options?.map((option, idx) => (
          <MenuItem
            key={`${option}-${idx}`}
            value={option}
            sx={{
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              width: isMobile ? '100%' : scaledDimensions.width,
            }}
          >
            {option}
          </MenuItem>
        ))}
      </Select>
      {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
    </FormControl>
  );
};

export default DropdownElement;
